/**
 * The Mute Chat notifications switch in Trip Settings. It flips as soon as it's clicked
 * and settles on the server's answer once nothing is in flight. Changes apply in the
 * order they started, so an older answer landing late never overrides a newer one, and a
 * load that overlapped a change is dropped, since it may have read the value from before
 * it. Ported from iOS `ChatMuteSetting`; every step returns a new setting.
 */
export interface ChatMuteSetting {
	/** What the switch shows. */
	readonly isMuted: boolean
	/** What the server last said; null until it has. */
	readonly confirmed: boolean | null
	/** What the switch was last flipped to. */
	readonly chosen: boolean
	readonly changesStarted: number
	readonly changesInFlight: number
	readonly lastChangeApplied: number
}

export interface MuteLoad {
	readonly changesStarted: number
}

export interface MuteChange {
	readonly sequence: number
}

export const initialMuteSetting: ChatMuteSetting = {
	isMuted: false,
	confirmed: null,
	chosen: false,
	changesStarted: 0,
	changesInFlight: 0,
	lastChangeApplied: 0,
}

/** Off until the server says, so the switch never acts on a guess. */
export const isMuteEnabled = (setting: ChatMuteSetting): boolean => setting.confirmed !== null

export const startMuteLoad = (setting: ChatMuteSetting): MuteLoad => ({ changesStarted: setting.changesStarted })

/** `isMuted` is null when the load failed. */
export const finishMuteLoad = (setting: ChatMuteSetting, load: MuteLoad, isMuted: boolean | null): ChatMuteSetting => {
	if (isMuted === null || setting.changesInFlight > 0 || load.changesStarted !== setting.changesStarted) return setting
	return { ...setting, confirmed: isMuted, isMuted }
}

export const startMuteChange = (
	setting: ChatMuteSetting,
	isMuted: boolean,
): { setting: ChatMuteSetting; change: MuteChange } => {
	const changesStarted = setting.changesStarted + 1
	return {
		setting: { ...setting, changesStarted, changesInFlight: setting.changesInFlight + 1, isMuted, chosen: isMuted },
		change: { sequence: changesStarted },
	}
}

/**
 * Call when the change lands; `isMuted` is the server's answer, null when it failed.
 * `flippedBack` says whether the switch settled away from what was last chosen, which is
 * worth telling: a failure that a newer change went on to cover says nothing.
 */
export const finishMuteChange = (
	setting: ChatMuteSetting,
	change: MuteChange,
	isMuted: boolean | null,
): { setting: ChatMuteSetting; flippedBack: boolean } => {
	let next = { ...setting, changesInFlight: setting.changesInFlight - 1 }
	if (isMuted !== null && change.sequence > next.lastChangeApplied)
		next = { ...next, lastChangeApplied: change.sequence, confirmed: isMuted }
	if (next.changesInFlight > 0 || next.confirmed === null) return { setting: next, flippedBack: false }
	return { setting: { ...next, isMuted: next.confirmed }, flippedBack: next.confirmed !== next.chosen }
}
